import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { add, format, parseISO } from "date-fns";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { green } from "@material-ui/core/colors";
import {
  Edit,
  CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  DeleteOutline,
  Facebook,
  Instagram,
  WhatsApp,
} from "@material-ui/icons";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  CircularProgress,
  Tooltip
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import formatSerializedId from "../../utils/formatSerializedId";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";
import { Can } from "../../components/Can";

// Helper Components
function CircularProgressWithLabel(props) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="caption" component="div" color="textSecondary">
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const CustomToolTip = ({ title, content, children }) => {
  return (
    <Tooltip
      arrow
      title={
        <React.Fragment>
          <Typography gutterBottom variant="body2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {content && <Typography variant="caption">{content}</Typography>}
        </React.Fragment>
      }
    >
      {children}
    </Tooltip>
  );
};

const IconChannel = (channel) => {
  switch (channel) {
    case "facebook":
      return <Facebook style={{ color: "#3b5998", fontSize: 20 }} />;
    case "instagram":
      return <Instagram style={{ color: "#e1306c", fontSize: 20 }} />;
    case "whatsapp":
      return <WhatsApp style={{ color: "#25d366", fontSize: 20 }} />;
    default:
      return null;
  }
};
// Continuação de Connections_part1.jsx

const Connections = () => {
  const themeV4 = useThemeV4();
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [statusImport, setStatusImport] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const history = useHistory();
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);
  const [planConfig, setPlanConfig] = useState(false);
  const [supportWhatsapp, setSupportWhatsapp] = useState("");

  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      setPlanConfig(planConfigs);
    }
    fetchData();
  }, [companyId, getPlanCompany]);

  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, userID } = response;
      api.post("/facebook", { facebookUserId: userID, facebookUserToken: accessToken })
        .then(() => toast.success(i18n.t("connections.facebook.success")))
        .catch((error) => toastError(error));
    }
  };

  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, userID } = response;
      api.post("/facebook", { addInstagram: true, facebookUserId: userID, facebookUserToken: accessToken })
        .then(() => toast.success(i18n.t("connections.facebook.success")))
        .catch((error) => toastError(error));
    }
  };

  useEffect(() => {
    socket.on(`importMessages-${user.companyId}`, (data) => {
      if (data.action === "refresh") {
        setStatusImport([]);
        history.go(0);
      }
      if (data.action === "update") {
        setStatusImport(data.status);
      }
    });
  }, [whatsApps, socket, user.companyId, history]);

  useEffect(() => {
    const fetchSupportWhatsapp = async () => {
      try {
        const { data } = await api.get("/global-config/public-branding");
        const fromPanel = data?.loginWhatsapp;
        const fromEnv = process.env.REACT_APP_NUMBER_SUPPORT
          ? `https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`
          : "";
        setSupportWhatsapp(fromPanel || fromEnv);
      } catch (err) {
        console.error("Erro ao carregar número de suporte:", err);
        if (process.env.REACT_APP_NUMBER_SUPPORT) {
          setSupportWhatsapp(`https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`);
        }
      }
    };
    fetchSupportWhatsapp();
  }, []);

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, []);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, []);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const openInNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
      });
    }
    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    if (action === "closedImported") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.closedImportedTitle"),
        message: i18n.t("connections.confirmationModal.closedImportedMessage"),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "closedImported") {
      try {
        await api.post(`/closedimported/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.closedimported"));
      } catch (err) {
        toastError(err);
      }
    }
    setConfirmModalInfo(confirmationModalInitialState);
  };

  const renderImportButton = (whatsApp) => {
    if (whatsApp?.statusImportMessages === "renderButtonCloseTickets") {
      return (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleOpenConfirmationModal("closedImported", whatsApp.id)}
          sx={{ ml: 1, fontSize: "0.75rem", textTransform: "none" }}
        >
          {i18n.t("connections.buttons.closedImported")}
        </Button>
      );
    }

    if (whatsApp?.importOldMessages) {
      let isTimeStamp = !isNaN(new Date(Math.floor(whatsApp?.statusImportMessages)).getTime());
      if (isTimeStamp) {
        const ultimoStatus = new Date(Math.floor(whatsApp?.statusImportMessages)).getTime();
        const dataLimite = +add(ultimoStatus, { seconds: +35 }).getTime();
        if (dataLimite > new Date().getTime()) {
          return (
            <Button
              disabled
              size="small"
              variant="outlined"
              endIcon={<CircularProgress size={12} />}
              sx={{ ml: 1, fontSize: "0.75rem", textTransform: "none" }}
            >
              {i18n.t("connections.buttons.preparing")}
            </Button>
          );
        }
      }
    }
  };

  const renderActionButtons = (whatsApp) => {
    return (
      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
        {whatsApp.status === "qrcode" && (
          <Can
            role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <Button
                size="small"
                variant="contained"
                onClick={() => handleOpenQrModal(whatsApp)}
                sx={{
                  bgcolor: PRIMARY_MAIN,
                  color: "#fff",
                  fontSize: "0.75rem",
                  textTransform: "none",
                  "&:hover": { bgcolor: PRIMARY_DARK }
                }}
              >
                {i18n.t("connections.buttons.qrcode")}
              </Button>
            )}
          />
        )}
        {whatsApp.status === "DISCONNECTED" && (
          <Can
            role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleStartWhatsAppSession(whatsApp.id)}
                  sx={{ fontSize: "0.75rem", textTransform: "none" }}
                >
                  {i18n.t("connections.buttons.tryAgain")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleRequestNewQrCode(whatsApp.id)}
                  sx={{ fontSize: "0.75rem", textTransform: "none" }}
                >
                  {i18n.t("connections.buttons.newQr")}
                </Button>
              </>
            )}
          />
        )}
        {(whatsApp.status === "CONNECTED" || whatsApp.status === "PAIRING" || whatsApp.status === "TIMEOUT") && (
          <Can
            role={user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleOpenConfirmationModal("disconnect", whatsApp.id)}
                  sx={{ fontSize: "0.75rem", textTransform: "none" }}
                >
                  {i18n.t("connections.buttons.disconnect")}
                </Button>
                {renderImportButton(whatsApp)}
              </>
            )}
          />
        )}
        {whatsApp.status === "OPENING" && (
          <Button
            size="small"
            variant="outlined"
            disabled
            sx={{ fontSize: "0.75rem", textTransform: "none" }}
          >
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
      </Stack>
    );
  };

  const renderStatusToolTips = (whatsApp) => {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        {whatsApp.status === "DISCONNECTED" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.disconnected.title")}
            content={i18n.t("connections.toolTips.disconnected.content")}
          >
            <SignalCellularConnectedNoInternet0Bar color="secondary" />
          </CustomToolTip>
        )}
        {whatsApp.status === "OPENING" && <CircularProgress size={24} />}
        {whatsApp.status === "qrcode" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.qrcode.title")}
            content={i18n.t("connections.toolTips.qrcode.content")}
          >
            <CropFree />
          </CustomToolTip>
        )}
        {whatsApp.status === "CONNECTED" && (
          <CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
            <SignalCellular4Bar style={{ color: green[500] }} />
          </CustomToolTip>
        )}
        {(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.timeout.title")}
            content={i18n.t("connections.toolTips.timeout.content")}
          >
            <SignalCellularConnectedNoInternet2Bar color="secondary" />
          </CustomToolTip>
        )}
      </Box>
    );
  };

  const restartWhatsapps = async () => {
    try {
      await api.post(`/whatsapp-restart/`);
      toast.success(i18n.t("connections.waitConnection"));
    } catch (err) {
      toastError(err);
    }
  };

  if (user.profile === "user" && user.allowConnections === "disabled") {
    return <ForbiddenPage />;
  }

  return (
    <Box sx={{ minHeight: "100vh", py: { xs: 3, md: 4 }, px: { xs: 2, md: 3 }, bgcolor: "#f8fafc" }}>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      {qrModalOpen && (
        <QrcodeModal
          open={qrModalOpen}
          onClose={handleCloseQrModal}
          whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
        />
      )}
      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />

      <Container maxWidth="xl">
        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <SettingsInputAntennaIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                {i18n.t("connections.title")} ({whatsApps.length})
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Gerencie suas conexões de canais
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="outlined"
              onClick={restartWhatsapps}
              sx={{
                borderRadius: 2,
                fontSize: "0.875rem",
                textTransform: "none",
                fontWeight: 600
              }}
            >
              {i18n.t("connections.restartConnections")}
            </Button>

            <Button
              variant="outlined"
              onClick={() => openInNewTab(supportWhatsapp)}
              sx={{
                borderRadius: 2,
                fontSize: "0.875rem",
                textTransform: "none",
                fontWeight: 600
              }}
            >
              {i18n.t("connections.callSupport")}
            </Button>

            <PopupState variant="popover" popupId="demo-popup-menu">
              {(popupState) => (
                <React.Fragment>
                  <Can
                    role={user.profile}
                    perform="connections-page:addConnection"
                    yes={() => (
                      <>
                        <Button
                          variant="contained"
                          {...bindTrigger(popupState)}
                          sx={{
                            bgcolor: PRIMARY_MAIN,
                            color: "#fff",
                            borderRadius: 2,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": { bgcolor: PRIMARY_DARK }
                          }}
                        >
                          {i18n.t("connections.newConnection")}
                        </Button>
                        <Menu {...bindMenu(popupState)}>
                          <MenuItem
                            disabled={planConfig?.plan?.useWhatsapp ? false : true}
                            onClick={() => {
                              handleOpenWhatsAppModal();
                              popupState.close();
                            }}
                          >
                            <WhatsApp fontSize="small" style={{ marginRight: "10px", color: "#25D366" }} />
                            WhatsApp
                          </MenuItem>
                          <FacebookLogin
                            appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                            autoLoad={false}
                            fields="name,email,picture"
                            version="9.0"
                            scope={
                              process.env.REACT_APP_REQUIRE_BUSINESS_MANAGEMENT?.toUpperCase() === "TRUE"
                                ? "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                                : "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement"
                            }
                            callback={responseFacebook}
                            render={(renderProps) => (
                              <MenuItem
                                disabled={planConfig?.plan?.useFacebook ? false : true}
                                onClick={renderProps.onClick}
                              >
                                <Facebook fontSize="small" style={{ marginRight: "10px", color: "#3b5998" }} />
                                Facebook
                              </MenuItem>
                            )}
                          />
                          <FacebookLogin
                            appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                            autoLoad={false}
                            fields="name,email,picture"
                            version="9.0"
                            scope={
                              process.env.REACT_APP_REQUIRE_BUSINESS_MANAGEMENT?.toUpperCase() === "TRUE"
                                ? "public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                                : "public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement"
                            }
                            callback={responseInstagram}
                            render={(renderProps) => (
                              <MenuItem
                                disabled={planConfig?.plan?.useInstagram ? false : true}
                                onClick={renderProps.onClick}
                              >
                                <Instagram fontSize="small" style={{ marginRight: "10px", color: "#e1306c" }} />
                                Instagram
                              </MenuItem>
                            )}
                          />
                        </Menu>
                      </>
                    )}
                  />
                </React.Fragment>
              )}
            </PopupState>
          </Stack>
        </Stack>

        {/* Status de Importação */}
        {statusImport?.all && (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3,
              bgcolor: "#fff",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              p: 3
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              {statusImport?.this === -1
                ? i18n.t("connections.buttons.preparing")
                : i18n.t("connections.buttons.importing")}
            </Typography>
            {statusImport?.this === -1 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 2, textAlign: "center" }}>
                  {`${i18n.t(`connections.typography.processed`)} ${statusImport?.this} ${i18n.t(`connections.typography.in`)} ${statusImport?.all}  ${i18n.t(`connections.typography.date`)}: ${statusImport?.date}`}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <CircularProgressWithLabel value={(statusImport?.this / statusImport?.all) * 100} />
                </Box>
              </>
            )}
          </Paper>
        )}

        {/* Tabela */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
              Todas as Conexões
            </Typography>
          </Box>

          <TableContainer sx={{ maxHeight: "calc(100vh - 320px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    Canal
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("connections.table.name")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("connections.table.number")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("connections.table.status")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("connections.table.session")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("connections.table.lastUpdate")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("connections.table.default")}
                  </TableCell>
                  <Can
                    role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
                    perform="connections-page:addConnection"
                    yes={() => (
                      <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                        {i18n.t("connections.table.actions")}
                      </TableCell>
                    )}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRowSkeleton columns={8} />
                ) : (
                  <>
                    {whatsApps?.length > 0 &&
                      whatsApps.map((whatsApp) => (
                        <TableRow
                          key={whatsApp.id}
                          sx={{ "&:hover": { bgcolor: "action.hover", transition: "background-color 0.2s" } }}
                        >
                          <TableCell align="center">{IconChannel(whatsApp.channel)}</TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                              {whatsApp.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                              {whatsApp.number && whatsApp.channel === "whatsapp" ? (
                                <>{formatSerializedId(whatsApp.number)}</>
                              ) : (
                                whatsApp.number
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{renderStatusToolTips(whatsApp)}</TableCell>
                          <TableCell align="center">{renderActionButtons(whatsApp)}</TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                              {format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {whatsApp.isDefault && (
                              <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <CheckCircle style={{ color: green[500] }} />
                              </Box>
                            )}
                          </TableCell>
                          <Can
                            role={user.profile}
                            perform="connections-page:addConnection"
                            yes={() => (
                              <TableCell align="center">
                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditWhatsApp(whatsApp)}
                                    sx={{
                                      color: "text.secondary",
                                      "&:hover": { color: PRIMARY_MAIN, bgcolor: "rgba(25, 118, 210, 0.08)" }
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenConfirmationModal("delete", whatsApp.id)}
                                    sx={{
                                      color: "text.secondary",
                                      "&:hover": { color: "#ef4444", bgcolor: "rgba(239, 68, 68, 0.08)" }
                                    }}
                                  >
                                    <DeleteOutline fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            )}
                          />
                        </TableRow>
                      ))}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </Box>
  );
};

export default Connections;